/**
 * MyCopro - SubscriptionBanner
 * Bannière d'avertissement pour les abonnements
 * 
 * ✅ NOUVEAU:
 * - Affiche la bannière TRIAL à partir du 20ème jour (J-10)
 * - Affiche la bannière GRACE avec compte à rebours
 * - Styles différenciés selon le statut
 */

import React, { useState } from 'react';
import { AlertTriangle, X, Clock, AlertCircle, Gift, Sparkles } from 'lucide-react';
import { useSubscription, formatExpirationDate } from '@/hooks/useSubscription';

interface SubscriptionBannerProps {
  /** Permettre de fermer temporairement la bannière */
  dismissible?: boolean;
  /** Classe CSS additionnelle */
  className?: string;
}

export const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({
  dismissible = true,
  className = ''
}) => {
  const { 
    showWarningBanner, 
    showTrialBanner,
    status, 
    warningMessage, 
    endDate, 
    daysRemaining,
    graceDaysRemaining
  } = useSubscription();
  
  const [isDismissed, setIsDismissed] = useState(false);

  // Ne rien afficher si pas de bannière à montrer ou si fermée
  // ✅ Afficher aussi pour trial (à partir de J-10)
  if ((!showWarningBanner && !showTrialBanner) || isDismissed) {
    return null;
  }

  // Déterminer le style selon le statut
  const getStyles = () => {
    switch (status) {
      case 'expired':
        return {
          bg: 'bg-red-500',
          text: 'text-white',
          icon: <AlertCircle size={20} />,
          iconBg: 'bg-red-600'
        };
      case 'grace':
        // Période de grâce - style orange/rouge selon urgence
        if (graceDaysRemaining !== null && graceDaysRemaining <= 2) {
          return {
            bg: 'bg-red-500',
            text: 'text-white',
            icon: <AlertCircle size={20} />,
            iconBg: 'bg-red-600'
          };
        }
        return {
          bg: 'bg-orange-500',
          text: 'text-white',
          icon: <AlertTriangle size={20} />,
          iconBg: 'bg-orange-600'
        };
      case 'trial':
        // Trial - style bleu/violet avec icône cadeau
        if (daysRemaining !== null && daysRemaining <= 3) {
          return {
            bg: 'bg-amber-500',
            text: 'text-white',
            icon: <Clock size={20} />,
            iconBg: 'bg-amber-600'
          };
        }
        return {
          bg: 'bg-blue-500',
          text: 'text-white',
          icon: <Gift size={20} />,
          iconBg: 'bg-blue-600'
        };
      case 'expiring':
        if (daysRemaining !== null && daysRemaining <= 7) {
          return {
            bg: 'bg-amber-500',
            text: 'text-white',
            icon: <AlertTriangle size={20} />,
            iconBg: 'bg-amber-600'
          };
        }
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-800',
          icon: <Clock size={20} />,
          iconBg: 'bg-amber-200'
        };
      default:
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-800',
          icon: <AlertTriangle size={20} />,
          iconBg: 'bg-blue-200'
        };
    }
  };

  // ✅ Construire le message approprié
  const getMessage = () => {
    if (status === 'trial') {
      if (daysRemaining === 0) {
        return "Dernier jour d'essai gratuit !";
      }
      if (daysRemaining === 1) {
        return "Plus qu'1 jour d'essai gratuit";
      }
      return `Il vous reste ${daysRemaining} jours d'essai gratuit`;
    }
    
    if (status === 'grace') {
      if (graceDaysRemaining === 0) {
        return "Dernier jour de grâce - accès suspendu demain !";
      }
      if (graceDaysRemaining === 1) {
        return "Plus qu'1 jour avant suspension de l'accès";
      }
      return `Période de grâce : ${graceDaysRemaining} jours restants`;
    }
    
    // Fallback au message standard
    return warningMessage;
  };

  const styles = getStyles();
  const message = getMessage();

  // ✅ Bannière non fermable pour expired et grace (2 derniers jours)
  const canDismiss = dismissible && 
    status !== 'expired' && 
    !(status === 'grace' && graceDaysRemaining !== null && graceDaysRemaining <= 2);

  return (
    <div className={`${styles.bg} ${styles.text} px-4 py-3 ${className}`}>
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-full ${styles.iconBg}`}>
            {styles.icon}
          </div>
          <div>
            <p className="font-medium text-sm">
              {message}
            </p>
            {endDate && status !== 'grace' && (
              <p className="text-xs opacity-80">
                {status === 'trial' ? "Fin de l'essai : " : "Date d'expiration : "}
                {formatExpirationDate(endDate)}
              </p>
            )}
            {status === 'grace' && (
              <p className="text-xs opacity-80">
                Activez votre abonnement pour continuer à utiliser O'Copro
              </p>
            )}
          </div>
        </div>
        
        {canDismiss && (
          <button
            onClick={() => setIsDismissed(true)}
            className="p-1 hover:bg-black/10 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Version compacte de la bannière pour les espaces réduits
 */
export const SubscriptionBannerCompact: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { showWarningBanner, showTrialBanner, status, daysRemaining, graceDaysRemaining } = useSubscription();

  if (!showWarningBanner && !showTrialBanner) {
    return null;
  }

  const getBadgeStyle = () => {
    switch (status) {
      case 'expired':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'grace':
        return graceDaysRemaining !== null && graceDaysRemaining <= 2
          ? 'bg-red-100 text-red-700 border-red-200'
          : 'bg-orange-100 text-orange-700 border-orange-200';
      case 'trial':
        return daysRemaining !== null && daysRemaining <= 3
          ? 'bg-amber-100 text-amber-700 border-amber-200'
          : 'bg-blue-100 text-blue-700 border-blue-200';
      case 'expiring':
        return daysRemaining !== null && daysRemaining <= 7
          ? 'bg-amber-100 text-amber-700 border-amber-200'
          : 'bg-amber-50 text-amber-600 border-amber-100';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getMessage = () => {
    if (status === 'expired') {
      return 'Accès suspendu';
    }
    if (status === 'grace') {
      if (graceDaysRemaining === 0) return 'Dernier jour';
      if (graceDaysRemaining === 1) return '1j avant suspension';
      return `Grâce: ${graceDaysRemaining}j`;
    }
    if (status === 'trial') {
      if (daysRemaining === 0) return 'Dernier jour essai';
      if (daysRemaining === 1) return '1j d\'essai';
      return `Essai: ${daysRemaining}j`;
    }
    if (daysRemaining === 0) return "Expire aujourd'hui";
    if (daysRemaining === 1) return 'Expire demain';
    return `Expire dans ${daysRemaining}j`;
  };

  const getIcon = () => {
    if (status === 'expired' || (status === 'grace' && graceDaysRemaining !== null && graceDaysRemaining <= 2)) {
      return <AlertCircle size={12} />;
    }
    if (status === 'trial') {
      return <Sparkles size={12} />;
    }
    return <Clock size={12} />;
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getBadgeStyle()} ${className}`}>
      {getIcon()}
      {getMessage()}
    </span>
  );
};

export default SubscriptionBanner;
